// auth-service bounds concurrent password hashing. When the bound is full it
// answers 503 {"error":"busy_retry_later"} with Retry-After (agion/Agion#2262).
// Sign-in and invitation registration wait and resend a bounded number of
// times before showing an error.

export const BUSY_CODE = 'busy_retry_later'
export const MAX_BUSY_RETRIES = 3
const DEFAULT_RETRY_AFTER_SECONDS = 1
const MAX_RETRY_AFTER_SECONDS = 10

export async function isBusyResponse(response: Response): Promise<boolean> {
  if (response.status !== 503) return false
  const body: unknown = await response.clone().json().catch(() => null)
  return body !== null && typeof body === 'object' && 'error' in body && body.error === BUSY_CODE
}

export function retryAfterMs(response: Response): number {
  const seconds = Number.parseInt(response.headers.get('Retry-After') ?? '', 10)
  const wait = Number.isFinite(seconds) && seconds > 0
    ? Math.min(seconds, MAX_RETRY_AFTER_SECONDS)
    : DEFAULT_RETRY_AFTER_SECONDS
  return wait * 1000
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// Sends the request, and while the answer is a busy refusal, waits for its
// Retry-After and sends it again, at most MAX_BUSY_RETRIES more times. The
// last response is returned; check it with isBusyResponse.
export async function fetchRetryingWhenBusy(send: () => Promise<Response>): Promise<Response> {
  let response = await send()
  for (let retry = 0; retry < MAX_BUSY_RETRIES && (await isBusyResponse(response)); retry++) {
    await sleep(retryAfterMs(response))
    response = await send()
  }
  return response
}
